import { useState, useRef, useEffect } from "react";
import { useStableBlobUrl } from "~/common/blob/useStableBlobUrl";
import ArtifactLightbox from "./ArtifactLightbox";
import type { WorkflowArtifact } from "./WorkflowArtifactsSection";

export interface HitlStep {
  step_key: string;
  screenshot_uri: string;
  /** Device-pixel region (DPR-scaled, matching the PNG) the substrate stamps at
   *  capture time — it frames the element the screenshot's coloured outline was
   *  drawn around (e.g. the auto-picked policy row). The preview crops to it.
   *  NOTE: this is STATIC — it frames the capture-time default and does NOT
   *  track a later radio-button change in the picker. (A per-choice
   *  selection-tracking overlay was never built: the choices' CSS-pixel bounds
   *  don't line up with the DPR-scaled screenshot — see
   *  test_verify_policy_detail_question.) */
  highlight_region?: { x: number; y: number; width: number; height: number };
}

export function HitlEvidencePreview({ steps }: { steps?: HitlStep[] }) {
  const step =
    [...(steps ?? [])].reverse().find((s) => !!s.screenshot_uri) ?? null;
  // Stabilize the screenshot URL across polls so a re-signed SAS doesn't
  // bust the browser's image cache (server-side BlobResultResigner already
  // reuses healthy SAS; this is the belt-and-braces UI guard).
  const screenshotUri = useStableBlobUrl(step?.screenshot_uri) ?? "";
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [naturalW, setNaturalW] = useState(0);
  const [containerW, setContainerW] = useState(0);
  const btnRef = useRef<HTMLButtonElement>(null);

  const region = step?.highlight_region ?? null;

  // Use new Image() rather than a hidden sr-only element — avoids browsers
  // not reporting naturalWidth for visually clipped/zero-size elements.
  useEffect(() => {
    if (!screenshotUri || !region) return;
    let cancelled = false;
    const img = new window.Image();
    img.onload = () => {
      if (!cancelled) setNaturalW(img.naturalWidth);
    };
    img.src = screenshotUri;
    return () => {
      cancelled = true;
    };
  }, [screenshotUri, region]);

  // Capture the button's rendered width and keep it in sync via ResizeObserver
  // so CroppedPreview has an accurate container width even on first paint
  // (a one-shot useEffect often fires before layout completes).
  useEffect(() => {
    const el = btnRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      setContainerW(entries[0].contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (!step) return null;

  const artifacts: WorkflowArtifact[] = [
    { url: screenshotUri, step_name: step.step_key, kind: "screenshot" },
  ];

  const showCrop = !!region && naturalW > 0;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="w-full rounded overflow-hidden border border-primary-gray-light-200 hover:border-sunrise transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-sunrise"
        onClick={() => setLightboxOpen(true)}
        aria-label="View evidence screenshot"
        data-testid="hitl-evidence-preview"
      >
        {showCrop ? (
          <CroppedPreview
            src={screenshotUri}
            region={region}
            naturalW={naturalW}
            containerW={containerW || 480}
          />
        ) : (
          <img
            src={screenshotUri}
            alt="Evidence screenshot"
            className="w-full max-h-24 object-contain object-top"
            onLoad={(e) => setNaturalW(e.currentTarget.naturalWidth)}
            data-testid="hitl-evidence-full"
          />
        )}
      </button>

      <ArtifactLightbox
        open={lightboxOpen}
        artifacts={artifacts}
        startIndex={0}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  );
}

function CroppedPreview({
  src,
  region,
  naturalW,
  containerW,
}: {
  src: string;
  region: { x: number; y: number; width: number; height: number };
  naturalW: number;
  containerW: number;
}) {
  const pad = 50;
  const cropX = region.x - pad;
  const cropY = region.y - pad;
  const cropW = region.width + pad * 2;
  const cropH = region.height + pad * 2;
  const scale = Math.min(containerW / cropW, 80 / cropH);
  const renderedW = Math.round(cropW * scale);
  const renderedH = Math.round(cropH * scale);

  return (
    <div
      className="flex items-center justify-center w-full overflow-hidden"
      style={{ height: 80 }}
      data-testid="hitl-evidence-cropped"
    >
      <div
        className="relative overflow-hidden flex-shrink-0"
        style={{ width: renderedW, height: renderedH }}
      >
        <img
          src={src}
          alt="Evidence screenshot"
          draggable={false}
          style={{
            position: "absolute",
            left: Math.round(-cropX * scale),
            top: Math.round(-cropY * scale),
            width: Math.round(naturalW * scale),
            maxWidth: "none",
          }}
        />
      </div>
    </div>
  );
}
